/** @format */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Col, Row, Card, Divider } from 'antd';
import { setGlobal, useGlobal } from 'reactn';

import { GlobalConsumer } from '../../../../../../components/Commons/Context/Global';
import { Service } from './service';

import GoogleMap from '../../../../../../components/Commons/Header/components/Address/components/GoogleMap';
import { geocodeAddress } from '../../../../../../components/Commons/Header/components/Address/utils/geocodeAddress';
import { AddressSetLocation } from '../../../../../../components/Commons/Hooks/Functions/AddressSetLocation';
import { PharmacySetLocation } from '../../../../../../components/Commons/Hooks/Functions/PharmacySetLocation';

import { CustomNotification } from '../../../../../../v2-components/Antd/Notification';
import { NOTIFICATION } from '../../../../../../v2-components/Antd/Notification/constant';

import CustomFormItem from '../../../../../../v2-components/Antd/FormItem';
import CustomSelect from '../../../../../../v2-components/Antd/Select';
import CustomInput from '../../../../../../v2-components/Antd/Input';
import CustomButton from '../../../../../../v2-components/Antd/Button';
import CustomCheckbox from '../../../../../../v2-components/Antd/Checkbox';

import { locationLabel } from '../../../../../../components/Commons/Header/components/Address/components/AddressSteps/handle/location-label.handle';

import { extractFirstStreetNumber } from '../../utils/extractFirstStreetNumber';

import './style.scss';

const SESSION_KEY_ADDRESS_WITHOUT_NUMBER = 'ph_checkout_withdrawal_addressWithoutNumber';

export function persistAddressWithoutNumber(checked) {
		sessionStorage.setItem(SESSION_KEY_ADDRESS_WITHOUT_NUMBER, String(!!checked));
}

export function readAddressWithoutNumberFromSession() {
		const v = sessionStorage.getItem(SESSION_KEY_ADDRESS_WITHOUT_NUMBER);
		if (v === null) return undefined;
		return v === 'true';
}

export default function CheckoutAddress({ form }) {
	const { isLocation, setLocation } = GlobalConsumer();
	// eslint-disable-next-line
	const [isAddressDisplay, setAddressDisplay] = useGlobal('address');
	const [isUserInfo] = useGlobal('userMainInfo');
	const isUserToken = useMemo(() => isUserInfo?.token || null, [isUserInfo]);
	const [, setNearPharmacy] = useGlobal('mapPharmacy');

	const [regionOptions, setRegionOptions] = useState([]);
	const [communeOptions, setCommuneOptions] = useState([]);

	const [selectedRegion, setSelectedRegion] = useState(undefined);
	const [selectedCommune, setSelectedCommune] = useState(undefined);
	const [directionValue, setDirectionValue] = useState('');

	const [coordinates, setCoordinates] = useState(null); // {lat,lng}
	const [newAddress, setNewAddress] = useState(null);

	const [isLoadingButton, setLoadingButton] = useState(false);
	const [isLoadingMap, setLoadingMap] = useState(false);

	const [saveAddressChecked, setSaveAddressChecked] = useState(false);
	const [aliasValue, setAliasValue] = useState('');
	const [isAddressSaved, setAddressSaved] = useState(false);

	const [userAddressList, setUserAddressList] = useState([]);
	const [formattedAddressOptions, setFormattedAddressOptions] = useState([]);
	const [selectedUserAlias, setSelectedUserAlias] = useState(null);
	const [isAddressListItemSelected, setAddressListItemSelected] = useState(false);
	const [isDepartamento, setIsDepartamento] = useState(false);
	const [isCasa, setIsCasa] = useState(false);

	const [isAddressValidated, setAddressValidated] = useGlobal('isAddressValidated');
	const [isDirectionValidation, setDirectionValidation] = useGlobal('directionValidation');
	const [isFromPayment, setFromPayment] = useGlobal('isFromPayment');
	const [isFromBag, setFromBag] = useGlobal('isFromBag');

	const getLocationStore = () => {
		try {
			const raw = localStorage.getItem('locationStore');
			return raw ? JSON.parse(raw) : null;
		} catch (e) {
			return null;
		}
	};

	const extractCoords = (loc) => {
		if (!loc) return null;

		const lat = loc.lat ?? loc.latitude ?? loc?.location?.lat ?? loc?.location?.latitude ?? loc?.location?.location?.lat;

		const lng = loc.lng ?? loc.longitude ?? loc?.location?.lng ?? loc?.location?.longitude ?? loc?.location?.location?.lng;

		if (typeof lat === 'number' && typeof lng === 'number') return { lat, lng };
		return null;
	};

	const buildFormFields = (loc) => {
		if (!loc) return '';

		if (loc.formFields) return loc.formFields;

		const region = loc.region || loc?.location?.region || '';
		const commune = loc.commune || loc?.location?.commune || '';
		const street = loc.streetName || loc.street || loc?.location?.streetName || loc?.location?.street || loc?.location?.formatted_address || '';

		const pieces = [region, commune, street].map((x) => `${x}`.trim()).filter(Boolean);
		return pieces.join(', ');
	};

	const setFormFromFormFields = useCallback(
		async (formFields) => {
			if (!formFields) return;

			const parts = formFields.split(',').map((i) => i.trim());
			const region = parts?.[0] || '';
			const commune = parts?.[1] || '';
			const direction = parts?.slice(2).join(', ').trim();

			setSelectedRegion(region || undefined);
			setSelectedCommune(commune || undefined);
			setDirectionValue(direction || '');

			form.setFieldsValue({
				region: region || undefined,
				commune: commune || undefined,
				direction: direction || '',
			});

			if (region) {
				try {
					const response = await new Service().getCommunesByRegion(region);
					const options = response.data.map((c) => ({ label: c, value: c }));
					setCommuneOptions(options);
				} catch (e) {}
			}
		},
		[form],
	);

	const ensureMapCoordinates = useCallback(async (loc) => {
		if (!loc) return;

		const c = extractCoords(loc);
		if (c) {
			setCoordinates(c);
			return;
		}

		const ff = buildFormFields(loc);
		if (!ff) return;

		setLoadingMap(true);
		try {
			const resp = await geocodeAddress(ff); // debe retornar {lat,lng}
			setCoordinates(resp);
		} catch (e) {
			console.log('ensureMapCoordinates geocode error:', e);
		} finally {
			setLoadingMap(false);
		}
	}, []);

	const handleDirectionChange = (e) => {
		setDirectionValue(e.target.value);
		invalidateAddress();
	};

	const handleCommuneChange = (value) => {
		setSelectedCommune(value);
		invalidateAddress();
	};

	const handleHouseTypeChange = (value) => {
		const dep = value === 'depto';
		const casa = value === 'casa';
		setIsDepartamento(dep);
		setIsCasa(casa);
		invalidateAddress();

		if (!dep && !casa) {
			form.setFieldsValue({ floorDeptTower: '' });
		}
	};

	const handleRegionChange = async (regionValue) => {
		const trimmed = (regionValue || '').trim();

		setSelectedRegion(trimmed || undefined);
		setSelectedCommune(undefined);
		form.setFieldsValue({ commune: undefined });
		setCommuneOptions([]);
		invalidateAddress();

		if (!trimmed) return;

		new Service()
			.getCommunesByRegion(regionValue)
			.then((response) => {
				const options = response.data.map((c) => ({ label: c, value: c }));
				setCommuneOptions(options);
			})
			.catch((e) => {
				CustomNotification({
					type: NOTIFICATION.TYPE.ERROR,
					message: NOTIFICATION.MESSAGE.ERROR,
					description: 'Hubo un problema actualizando las comunas',
				});
				console.log('error in getCommunesByRegion', e);
			});
	};
	const handleUserAddressChange = async (value) => {
		setSelectedUserAlias(value);
		form.setFieldsValue({ addressList: value });

		// Nueva dirección
		if (value === '') {
			setAddressListItemSelected(false);

			setSelectedRegion(undefined);
			setSelectedCommune(undefined);
			setDirectionValue('');

			setSaveAddressChecked(false);
			setAliasValue('');
			setAddressSaved(false);

			setNewAddress(null);

			persistAddressWithoutNumber(false);

			form.setFieldsValue({
				region: undefined,
				commune: undefined,
				direction: '',
				alias: '',

				houseType: undefined,
				floorDeptTower: '',
				reference_ad: '',
				addressWithoutNumber: false,
			});

			return;
		}

		const index = userAddressList.findIndex((item) => item?.location?.formatted_address === value);
		if (index === -1) return;

		const selectedAddress = userAddressList[index];
		const formFields = selectedAddress?.formFields;

		setAddressListItemSelected(true);

		setSaveAddressChecked(false);
		setAliasValue('');
		setAddressSaved(false);
		form.setFieldsValue({ alias: '' });

		await setFormFromFormFields(formFields);

		persistAddressWithoutNumber(false);

		form.setFieldsValue({
			houseType: selectedAddress?.houseType ?? undefined,
			floorDeptTower: selectedAddress?.floorDeptTower ?? '',
			reference_ad: selectedAddress?.reference ?? '',
			addressWithoutNumber: false,
		});

		console.log('handleUserAddressChange - selectedAddress:', selectedAddress);

		// (Opcional) si quieres reflejar el map:
		await ensureMapCoordinates({ formFields });
		invalidateAddress();
	};

	const handleGetAddress = async () => {
		try {
			await form.validateFields(['region', 'commune', 'direction', 'houseType', 'floorDeptTower']);
		} catch (err) {
			return;
		}

		setLoadingButton(true);

		const region = form.getFieldValue('region') || '';
		const commune = form.getFieldValue('commune') || '';
		const direction = form.getFieldValue('direction') || '';
		const addressString = `${region}, ${commune}, ${direction}`;

		try {
			const responseCoordinates = await geocodeAddress(addressString);
			const address = await AddressSetLocation(addressString);

			if (address && address.lat && address.lng && responseCoordinates) {
				setAddressValidated(true);
				setDirectionValidation(false);
				setCoordinates(responseCoordinates);
				setNewAddress(address);
				setLocation(address);

				const noStreetNumber = form.getFieldValue('addressWithoutNumber');
				if (noStreetNumber) {
					form.setFieldsValue({ street_number: 'S/N' });
				} else {
					const d = `${form.getFieldValue('direction') ?? ''}`.trim();
					const num = extractFirstStreetNumber(d);
					form.setFieldsValue({ street_number: num || 'S/N' });
				}

				await applyAddress(address);

				CustomNotification({
					type: NOTIFICATION.TYPE.INFO,
					message: null,
					description: 'Dirección validada y aplicada',
				});
			} else {
				throw new Error('No se pudo obtener la dirección');
			}
		} catch (error) {
			console.log('error:', error);
			setNewAddress(null);

			CustomNotification({
				type: 'error',
				message: null,
				description: 'La dirección que estás intentando buscar no existe',
			});
		} finally {
			setLoadingButton(false);
		}
	};

	const saveNewAddress = async () => {
		if (!isUserInfo || !isUserToken || !saveAddressChecked) return true;

		if (!aliasValue) {
			CustomNotification({
				type: NOTIFICATION.TYPE.ERROR,
				message: NOTIFICATION.MESSAGE.ERROR,
				description: 'Por favor, ingresa una etiqueta para la dirección',
			});
			return false;
		}

		const region = form.getFieldValue('region') || '';
		const commune = form.getFieldValue('commune') || '';
		const direction = form.getFieldValue('direction') || '';
		const houseType = form.getFieldValue('houseType') || ''; // Tipo de vivienda
		const floorDeptTower = form.getFieldValue('floorDeptTower') || ''; // Piso/Depto/Torre
		const reference = form.getFieldValue('reference_ad') || ''; // Referencia

		const formFields = `${region}, ${commune}, ${direction}`;

		try {
			const newLocation = await AddressSetLocation(formFields, aliasValue);

			const newAddressToSave = {
				alias: aliasValue,
				location: newLocation.location,
				formFields,
				houseType, // Tipo de vivienda
				floorDeptTower, // Piso/Depto/Torre
				reference, // Referencia
			};

			const exists = userAddressList.some((a) => a?.location?.formatted_address === newAddressToSave?.location?.formatted_address);

			const updated = exists ? userAddressList : [...userAddressList, newAddressToSave];
			setUserAddressList(updated);

			const options = updated.map((address, i) => ({
				label: address.alias,
				value: address.location.formatted_address,
				key: `address-${i}-${address.location.formatted_address}`,
			}));

			if (options.length !== 0) {
				options.unshift({ label: 'Nueva dirección', value: '', key: 'new-address' });
			}

			setFormattedAddressOptions(options);

			setSelectedUserAlias(newAddressToSave.location.formatted_address);
			form.setFieldsValue({ addressList: newAddressToSave.location.formatted_address });

			setAddressListItemSelected(true);

			await new Service().saveAddress(isUserToken, updated);

			setAddressSaved(true);

			return true;
		} catch (e) {
			console.log('saveAddress error', e);
			setAddressSaved(false);

			CustomNotification({
				type: NOTIFICATION.TYPE.ERROR,
				message: NOTIFICATION.MESSAGE.ERROR,
				description: 'No se pudo guardar la dirección',
			});

			return false;
		}
	};

	const applyAddress = async (addressToApply) => {
		if (!addressToApply) return;

		const extra = {
			houseType: form.getFieldValue('houseType'),
			floorDeptTower: form.getFieldValue('floorDeptTower'),
			reference_ad: form.getFieldValue('reference_ad'),
		};

		const merged = { ...addressToApply, ...extra };

		await PharmacySetLocation(merged).then((response) => setNearPharmacy(response));

		setLocation(merged);
		setGlobal({ collapseMapPharmacies: [] });
		setGlobal(() => ({ userAddressList: merged }));
		setAddressDisplay(merged);

		try {
			localStorage.setItem('locationStore', JSON.stringify(merged));
		} catch (e) {}
	};

	const handleSaveAddressClick = async () => {
		if (!saveAddressChecked || !newAddress) return;

		try {
			await form.validateFields(['alias']);
		} catch (e) {
			return;
		}

		setLoadingButton(true);
		try {
			const ok = await saveNewAddress();
			if (!ok) return;
			CustomNotification({
				type: NOTIFICATION.TYPE.INFO,
				message: 'Dirección guardada con éxito',
				description: 'La dirección se ha guardado correctamente en tus direcciones.',
			});
		} catch (error) {
			console.log('Error guardando la dirección:', error);
			CustomNotification({
				type: NOTIFICATION.TYPE.ERROR,
				message: 'Error al guardar la dirección',
				description: 'Hubo un problema al intentar guardar la dirección.',
			});
		} finally {
			setLoadingButton(false);
		}
	};

	useEffect(() => {
		if (isUserInfo?.token) return;

		const CANDIDATE_KEYS = ['userMainInfo', 'userInfo', 'user', 'authUser', 'sessionUser'];

		let found = null;

		for (const k of CANDIDATE_KEYS) {
			try {
				const raw = localStorage.getItem(k);
				if (!raw) continue;
				const parsed = JSON.parse(raw);

				const token = parsed?.token || parsed?.data?.token || parsed?.user?.token;
				if (token) {
					found = parsed?.token ? parsed : parsed?.data ? parsed.data : parsed.user;
					break;
				}
			} catch (e) {}
		}

		if (found?.token) {
			setGlobal({ userMainInfo: found });
		}
		// eslint-disable-next-line
	}, []);

	useEffect(() => {
		if (isFromPayment || isFromBag) {
			setFromPayment(false);
			setFromBag(false);
			const ls = getLocationStore();

			const baseLoc = isLocation?.formFields ? isLocation : ls;

			if (!baseLoc) return;

			console.log('CheckoutAddress useEffect - baseLoc:', baseLoc);

			// setAddressDisplay(baseLoc);

			const ff = buildFormFields(baseLoc);
			if (ff) setFormFromFormFields(ff);

			const houseType = baseLoc?.houseType ?? undefined;
			const floorDeptTower = baseLoc?.floorDeptTower ?? '';
			const reference_ad = baseLoc?.reference_ad ?? baseLoc?.reference ?? '';

			form.setFieldsValue({
				houseType,
				floorDeptTower: houseType === 'depto' ? floorDeptTower : '',
				reference_ad,
			});

			setIsDepartamento(houseType === 'depto');

			ensureMapCoordinates(baseLoc);
		}
		// eslint-disable-next-line
	}, [isLocation?.formFields]);

	useEffect(() => {
		const ls = getLocationStore();

		const baseLoc = isLocation?.formFields ? isLocation : ls;

		if (!baseLoc) return;

		ensureMapCoordinates(baseLoc);
		// eslint-disable-next-line
	}, []);

	useEffect(() => {
		if (regionOptions.length !== 0) return;

		new Service()
			.getRegionsByCountry()
			.then((response) => {
				const options = response.data.map((r) => ({ label: r, value: r }));
				setRegionOptions(options);
			})
			.catch((e) => {
				CustomNotification({
					type: NOTIFICATION.TYPE.ERROR,
					message: NOTIFICATION.MESSAGE.ERROR,
					description: 'Hubo un problema cargando las regiones',
				});
				console.log('error regions', e);
			});
	}, [regionOptions.length]);

	useEffect(() => {
		const getUserAddress = async () => {
			if (!isUserToken) return;

			try {
				const addresses = await new Service().getUserdAddress(isUserToken);
				setUserAddressList(addresses);

				const options = addresses.map((address, i) => ({
					label: address.alias,
					value: address.location.formatted_address,
					key: `address-${i}-${address.location.formatted_address}`,
				}));

				if (options.length !== 0) {
					options.unshift({ label: 'Nueva dirección', value: '', key: 'new-address' });
				}

				setFormattedAddressOptions(options);
			} catch (error) {
				console.error('Error al obtener direcciones del usuario', error);
			}
		};

		getUserAddress();
	}, [isUserToken]);

	useEffect(() => {
		const current = form.getFieldValue('houseType');
		setIsDepartamento(current === 'depto');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [form.getFieldValue('houseType')]);

	const invalidateAddress = useCallback(() => {
		setAddressValidated(false);

		setNewAddress(null);

		setSaveAddressChecked(false);
		setAliasValue('');
		setAddressSaved(false);

		form.setFieldsValue({ addressValidated: false });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [form]);

	const ls = getLocationStore();
	const hasAnyAddress = !!isLocation?.formFields || !!buildFormFields(ls);

	return (
		<Card className="ph-checkout-address-card" title="Datos para la entrega">
			<Divider style={{ marginBottom: '17px', marginTop: '1px' }}></Divider>
			<div className="ph-checkout-address-map">
				{coordinates ? (
					<GoogleMap coordinates={coordinates} />
				) : hasAnyAddress ? (
					<div className="ph-checkout-address-map-placeholder">
						<p>{isLoadingMap ? 'Cargando mapa...' : 'No se pudo cargar el mapa para la dirección actual.'}</p>
					</div>
				) : (
					<div className="ph-checkout-address-map-placeholder">
						<p>Necesitas una dirección base para mostrar el mapa.</p>
					</div>
				)}
			</div>

			<div className="ph-checkout-address-form">
				{isUserToken && userAddressList.length > 0 && (
					<div className="ph-checkout-address-field ph-checkout-address-field--full">
						<CustomFormItem
							label="Elige entre tus direcciones o agrega una nueva dirección"
							name="addressList"
							labelCol={{ span: 24 }}
							wrapperCol={{ span: 24 }}
						>
							<CustomSelect
								placeholder="Elegir dirección guardada"
								onChange={handleUserAddressChange}
								value={selectedUserAlias}
								options={formattedAddressOptions}
								className={'ph-withdrawal-personal-data-field-selector'}
							/>
						</CustomFormItem>
					</div>
				)}

				<Row gutter={[12, 0]}>
					<Col xs={24} md={12}>
						<div>
							<CustomFormItem
								label={`${locationLabel('region')}`}
								labelCol={{ span: 24 }}
								wrapperCol={{ span: 24 }}
								rules={[{ required: true, message: 'Por favor, ingresa la región' }]}
								name="region"
							>
								<CustomSelect
									showSearch
									placeholder="Elegir región"
									onChange={handleRegionChange}
									value={selectedRegion}
									options={regionOptions}
									disabled={isAddressListItemSelected}
									className={'ph-withdrawal-personal-data-field-selector'}
								/>
							</CustomFormItem>
						</div>
					</Col>

					<Col xs={24} md={12}>
						<div>
							<CustomFormItem
								label={`${locationLabel('commune')}`}
								name="commune"
								labelCol={{ span: 24 }}
								wrapperCol={{ span: 24 }}
								rules={[{ required: true, message: 'Por favor, ingresa la comuna' }]}
							>
								<CustomSelect
									showSearch
									placeholder="Elegir comuna"
									onChange={handleCommuneChange}
									value={selectedCommune}
									options={communeOptions}
									disabled={isAddressListItemSelected || !selectedRegion}
									className={'ph-withdrawal-personal-data-field-selector'}
								/>
							</CustomFormItem>
						</div>
					</Col>
				</Row>

				<div>
					<CustomFormItem
						name="direction"
						label="Calle y número"
						labelCol={{ span: 24 }}
						wrapperCol={{ span: 24 }}
						dependencies={['addressWithoutNumber']}
						validateTrigger={['onChange', 'onBlur']}
						rules={[
							{ required: true, message: 'Por favor, ingresa la calle o dirección' },
							({ getFieldValue }) => ({
								validator(_, value) {
									if (getFieldValue('addressWithoutNumber')) {
										return Promise.resolve();
									}
									const v = (value || '').trim().replace(/\s+/g, ' ');
									if (!v) return Promise.resolve();

									const forbidden = /\b(casa|dpto|departamento|parcela|lote|depto)\b/i;
									if (forbidden.test(v)) {
										return Promise.reject(new Error('Solo debe escribir calle y numero'));
									}

									const hasStreetAndNumber = /.+\s+\d+/.test(v);
									if (!hasStreetAndNumber) {
										return Promise.reject(new Error('Falta colocar el número de calle'));
									}

									return Promise.resolve();
								},
							}),
						]}
					>
						<CustomInput
							placeholder="Calle y número"
							value={directionValue}
							onChange={handleDirectionChange}
							disabled={isAddressListItemSelected}
							className={'ph-withdrawal-personal-data-field-input'}
						/>
					</CustomFormItem>
				</div>
				<div className="ph-checkout-address-no-number">
					<CustomFormItem name="addressWithoutNumber" valuePropName="checked" initialValue={false}>
						<CustomCheckbox
							onChange={(e) => {
								persistAddressWithoutNumber(e.target.checked);
								invalidateAddress();
								form.validateFields(['direction']).catch(() => {});
							}}
						>
							Esta dirección no tiene número
						</CustomCheckbox>
					</CustomFormItem>
				</div>
				<Row gutter={[12, 0]}>
					{/* 25%: Tipo de vivienda */}
					<Col xs={24} md={6}>
						<div>
							<CustomFormItem
								name="houseType"
								label="Tipo de vivienda"
								labelCol={{ span: 24 }}
								wrapperCol={{ span: 24 }}
								rules={[{ required: true, message: 'Selecciona el tipo de vivienda' }]}
							>
								<CustomSelect
									placeholder="Tipo"
									disabled={false}
									options={[
										{ label: 'Casa', value: 'casa' },
										{ label: 'Departamento', value: 'depto' },
									]}
									onChange={handleHouseTypeChange}
									className={'ph-withdrawal-personal-data-field-selector'}
								/>
							</CustomFormItem>
						</div>
					</Col>

					{/* 25%: Piso/Depto./Torre — obligatorio para depto, opcional para casa, oculto si no aplica */}

					<Col xs={24} md={6} style={{ display: isDepartamento || isCasa ? 'block' : 'none' }}>
						<div className="ph-checkout-address-field">
							<CustomFormItem
								name="floorDeptTower"
								label="Piso/Depto./Torre"
								labelCol={{ span: 24 }}
								wrapperCol={{ span: 24 }}
								rules={[{ required: isDepartamento, message: 'Ingresa piso/depto/torre' }]}
							>
								<CustomInput
									placeholder="Ej: 12B / Torre 2"
									disabled={false}
									onChange={invalidateAddress}
									className={'ph-withdrawal-personal-data-field-input'}
								/>
							</CustomFormItem>
						</div>
					</Col>

					{/* 50%: Referencia */}
					<Col xs={24} md={12}>
						<div className="ph-checkout-address-field">
							<CustomFormItem name="reference_ad" label="Referencia" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }} rules={[{ required: false }]}>
								<CustomInput
									placeholder="Ej: Portón negro, frente a la plaza..."
									disabled={false}
									onChange={invalidateAddress}
									className={'ph-withdrawal-personal-data-field-input'}
								/>
							</CustomFormItem>
						</div>
					</Col>
				</Row>
				<p> *Datos obligatorios </p>

				<div className="ph-checkout-address-actions">
					<CustomButton className="ph-checkout-address-btn enabled-button" onClick={handleGetAddress} loading={isLoadingButton} disabled={isAddressValidated}>
						<p className="ph-checkout-text-btn">{isAddressValidated ? 'Dirección validada' : 'Validar dirección'}</p>
					</CustomButton>
				</div>
				{isDirectionValidation && <p className="ph-checkout-type-delivery-card-footer-advertise-validation"> Falta validar la dirección </p>}

				{isUserToken && !isAddressListItemSelected && (
					<div className="ph-checkout-address-save">
						<CustomFormItem name="saveAddress">
							<CustomCheckbox checked={saveAddressChecked} onChange={(e) => setSaveAddressChecked(e.target.checked)} disabled={!newAddress || isAddressSaved}>
								Guardar en mis direcciones
							</CustomCheckbox>
						</CustomFormItem>

						<CustomFormItem
							name="alias"
							rules={[
								{
									required: saveAddressChecked,
									message: 'Por favor, ingresa una etiqueta para la dirección',
								},
							]}
						>
							<CustomInput
								placeholder="Etiqueta"
								value={aliasValue}
								onChange={(e) => setAliasValue(e.target.value)}
								disabled={!saveAddressChecked || isAddressSaved || !newAddress}
								className={'ph-withdrawal-personal-data-field-input'}
							/>
						</CustomFormItem>

						{saveAddressChecked && (
							<CustomButton
								className="ph-checkout-address-btn enabled-button"
								onClick={handleSaveAddressClick}
								loading={isLoadingButton}
								disabled={!newAddress || !aliasValue || isAddressSaved}
							>
								<p className="ph-checkout-text-btn">Guardar dirección</p>
							</CustomButton>
						)}
					</div>
				)}
			</div>
		</Card>
	);
}
